import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Download,
} from "lucide-react";
import { downloadCSV } from "@/lib/export-utils";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/_core/hooks/useAuth";

export default function VATReport() {
  const { selectedCompanyId } = useCompany();
  const { isAuthenticated } = useAuth();

  // Date range state (default to current month)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of month
    return date.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  // Fetch VAT report
  const {
    data: report,
    isLoading,
    refetch,
  } = trpc.vat.getVATReport.useQuery(
    {
      companyId: selectedCompanyId || 0,
      startDate: new Date(startDate).getTime(),
      endDate: new Date(endDate).getTime(),
    },
    {
      enabled: isAuthenticated && !!selectedCompanyId,
    }
  );

  // Format currency
  const formatCurrency = (cents: number) => {
    const amount = cents / 100;
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const handleExportCSV = () => {
    if (!report) return;

    const lines: string[] = [];
    lines.push("Section,Vendor/Customer,Transactions,Total VAT (CHF)");

    // Input VAT rows
    for (const item of report.vendorVATBreakdown) {
      lines.push(
        `"Input VAT","${item.vendorName.replace(/"/g, '""')}",${item.transactionCount},${(item.totalVAT / 100).toFixed(2)}`
      );
    }

    // Output VAT rows
    for (const item of report.customerVATBreakdown) {
      lines.push(
        `"Output VAT","${item.customerName.replace(/"/g, '""')}",${item.transactionCount},${(item.totalVAT / 100).toFixed(2)}`
      );
    }

    // Summary
    lines.push("");
    lines.push(`"Total Input VAT",,,"${(report.inputVAT / 100).toFixed(2)}"`);
    lines.push(`"Total Output VAT",,,"${(report.outputVAT / 100).toFixed(2)}"`);
    lines.push(
      `"Net VAT Position",,,"${(report.netVATPosition / 100).toFixed(2)}"`
    );

    const csv = lines.join("\n") + "\n";
    const filename = `vat-report_${startDate}_${endDate}.csv`;
    downloadCSV(csv, filename);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">VAT Report (MWST)</h1>
          <p className="text-muted-foreground mt-2">
            Swiss Value Added Tax calculation with input and output VAT tracking
          </p>
        </div>

        {/* Date Range Filter */}
        <Card>
          <CardHeader>
            <CardTitle>Reporting Period</CardTitle>
            <CardDescription>
              Select the date range for your VAT report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleGenerateReport} disabled={isLoading}>
                  {isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Generate Report
                </Button>
                {report && (
                  <Button
                    variant="outline"
                    onClick={handleExportCSV}
                    aria-label="Export VAT report as CSV"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {report && (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              {/* Input VAT (Deductible) */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Input VAT (Vorsteuer)
                  </CardTitle>
                  <TrendingDown className="h-4 w-4 text-success" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-success">
                    {formatCurrency(report.inputVAT)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Deductible VAT from expenses
                  </p>
                </CardContent>
              </Card>

              {/* Output VAT (Owed) */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Output VAT (Umsatzsteuer)
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-destructive" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-destructive">
                    {formatCurrency(report.outputVAT)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    VAT collected from customers
                  </p>
                </CardContent>
              </Card>

              {/* Net VAT Position */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Net VAT Position
                  </CardTitle>
                  <DollarSign
                    className={`h-4 w-4 ${
                      report.netVATPosition > 0
                        ? "text-destructive"
                        : "text-success"
                    }`}
                  />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${
                      report.netVATPosition > 0
                        ? "text-destructive"
                        : "text-success"
                    }`}
                  >
                    {formatCurrency(Math.abs(report.netVATPosition))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {report.netVATPosition > 0
                      ? "Amount owed to tax authority"
                      : "Refund from tax authority"}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Input VAT Breakdown by Vendor */}
            {report.vendorVATBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Input VAT by Vendor</CardTitle>
                  <CardDescription>
                    Deductible VAT from expense transactions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">
                          Transactions
                        </TableHead>
                        <TableHead className="text-right">Total VAT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.vendorVATBreakdown.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            {item.vendorName}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.transactionCount}
                          </TableCell>
                          <TableCell className="text-right font-medium text-success">
                            {formatCurrency(item.totalVAT)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell>Total Input VAT</TableCell>
                        <TableCell className="text-right">
                          {report.vendorVATBreakdown.reduce(
                            (sum, item) => sum + item.transactionCount,
                            0
                          )}
                        </TableCell>
                        <TableCell className="text-right text-success">
                          {formatCurrency(report.inputVAT)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Output VAT Breakdown by Customer */}
            {report.customerVATBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Output VAT by Customer</CardTitle>
                  <CardDescription>
                    VAT collected from revenue transactions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-right">
                          Transactions
                        </TableHead>
                        <TableHead className="text-right">Total VAT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.customerVATBreakdown.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            {item.customerName}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.transactionCount}
                          </TableCell>
                          <TableCell className="text-right font-medium text-destructive">
                            {formatCurrency(item.totalVAT)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell>Total Output VAT</TableCell>
                        <TableCell className="text-right">
                          {report.customerVATBreakdown.reduce(
                            (sum, item) => sum + item.transactionCount,
                            0
                          )}
                        </TableCell>
                        <TableCell className="text-right text-destructive">
                          {formatCurrency(report.outputVAT)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {report.vendorVATBreakdown.length === 0 &&
              report.customerVATBreakdown.length === 0 && (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      No VAT transactions found
                    </h3>
                    <p className="text-muted-foreground text-center max-w-md">
                      No transactions with vendor or customer associations were
                      found for the selected period. Create transactions with
                      vendor/customer links to track VAT.
                    </p>
                  </CardContent>
                </Card>
              )}
          </>
        )}

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">
                  Calculating VAT report...
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
